/**
 * The home surface (`/`): the Syncra workspace dashboard of the reference mockup, rendered in
 * the product's dark tokens over real data. The calendar listing (ADR 0008) feeds every
 * section - «Сегодня» meetings with live badges and room entry, the «Ближайшее событие» card,
 * the month widget marks and the derived notification feed - through one fetched window.
 * The retired /dashboard and /calendar paths client-side redirect here.
 *
 * Identity is the shared SSO session (vc.ssoSession): without it the page still renders the
 * shell and the guest-capable surfaces (the folded join form, quick actions), while the
 * calendar-backed sections collapse into a sign-in prompt. The sign-in is the calendar PKCE
 * flow redirected back to /?sso=callback (the Keycloak client allows any SPA path);
 * the token renews itself through the shared auto-refresh scheduler and the 401-recovery
 * wrapper, exactly like the rest of the app.
 *
 * Layout discipline (no horizontal overflow at any width >= 360px): the sidebar is a full
 * navigation from lg, an icon-only rail on md..lg and an off-canvas drawer below md; grids are
 * auto-fit/minmax; every panel is width-capped with overflow handling.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  CalendarDays,
  ChevronRight,
  FileVideo2,
  Link2,
  Loader2,
  LogIn,
  LogOut,
  Menu,
  Plus,
  Radio,
  Settings as SettingsIcon,
  Video,
  X,
  Zap,
} from 'lucide-react';

import { BrandLockup, BrandMark } from '../components/brand-mark';
import { LangSwitch } from '../components/LangSwitch';
import { Button } from '../components/ui/button';
import { UserAvatar } from '../components/user-avatar';
import { navigateTo } from '../lib/spa';
import type { KeyValueStorage } from '../room/devicePrefs';
import {
  browserSsoStorage,
  callWithSsoRecovery,
  loadSsoSession,
  refreshSsoSession,
  startSsoAutoRefresh,
} from '../auth/sso';
import { CalendarApiError, listEvents, type CalendarEvent } from '../calendar/api';
import { createRoom } from '../api/rooms';
import { CalendarWidget } from '../calendar/CalendarWidget';
import { EventSidebar } from '../calendar/EventSidebar';
import { ScheduleDialog } from '../calendar/ScheduleDialog';
import { showToast, ToastStack } from '../calendar/Toast';
import {
  checkSession,
  completeCalendarSignIn,
  forgetSession,
  parseCalendarCallback,
  sessionUsername,
  startCalendarSignIn,
  calendarSsoConfig,
} from '../calendar/signIn';
import { dayKey, eventEnd, eventKey, eventStart, isEventLive } from '../calendar/month';
import {
  dataWindow,
  deriveNotifications,
  monthEventDays,
  nearestEvent,
  todaysEvents,
} from './data';
import { JoinRoomCard } from './JoinRoomCard';
import { useSidebarMode } from './useSidebarMode';

/** The SPA path of this page (the app root); also the PKCE sign-in return path. */
export const HOME_PATH = '/';

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly events: readonly CalendarEvent[] }
  | { readonly kind: 'error' };

/** Guest means "no usable session": the shell stays, calendar sections show the sign-in prompt. */
type DashboardSession = 'signed_in' | 'guest' | 'exchanging';

export interface DashboardPageProps {
  /** Injected for tests; defaults to the browser's shared SSO store (localStorage-backed). */
  readonly storage?: KeyValueStorage | null;
  /** Injected for tests; defaults to the wall clock. */
  readonly now?: Date;
  /** Fetch override for tests; defaults to the global fetch. */
  readonly fetchImpl?: typeof fetch;
}

export function DashboardPage({ storage, now, fetchImpl }: DashboardPageProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const store = useMemo(() => storage ?? browserSsoStorage(), [storage]);
  const at = useMemo(() => now ?? new Date(), [now]);
  // Live clock for the session lifecycle helpers; `at` stays the rendered "today" snapshot.
  const nowMs = useCallback((): number => (now !== undefined ? now.getTime() : Date.now()), [now]);
  const doFetch = fetchImpl ?? globalThis.fetch;
  const sidebarMode = useSidebarMode();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Sign-in callback handling runs exactly once per page load (StrictMode double-mounts).
  const callbackConsumed = useRef(false);
  const hasCallback = parseCalendarCallback(window.location.search).kind !== 'none';

  const [session, setSession] = useState<DashboardSession>(() => {
    if (hasCallback) {
      return 'exchanging';
    }
    if (store === null) {
      return 'guest';
    }
    return checkSession(store, at) === 'signed_in' ? 'signed_in' : 'guest';
  });
  const [token, setToken] = useState<string | null>(() => {
    if (hasCallback || store === null) {
      return null;
    }
    return checkSession(store, at) === 'signed_in'
      ? (loadSsoSession(store)?.accessToken ?? null)
      : null;
  });
  const [username, setUsername] = useState<string | null>(null);

  const [month, setMonth] = useState<Date>(() => new Date(at.getFullYear(), at.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(
    () => new Date(at.getFullYear(), at.getMonth(), at.getDate()),
  );
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [detailsEvent, setDetailsEvent] = useState<CalendarEvent | null>(null);

  const range = useMemo(() => dataWindow(at, month), [at, month]);
  const rangeFrom = range.from.getTime();
  const rangeTo = range.to.getTime();

  // A bearer the service rejected after the recovery wrapper: the rotation chain is dead.
  // The dashboard drops to the guest prompt instead of auto-redirecting through a silent
  // re-login (the /calendar page owns that flow); the visible sign-in button restarts it.
  const handleUnauthorized = useCallback((): void => {
    if (store !== null) {
      forgetSession(store);
    }
    setToken(null);
    setSession('guest');
  }, [store]);

  // Auto-refresh scheduler: rotates the token pair near expiry while the tab is open.
  useEffect(() => {
    if (store === null || session !== 'signed_in') {
      return undefined;
    }
    return startSsoAutoRefresh({
      config: calendarSsoConfig(),
      storage: store,
      fetchImpl: doFetch,
      nowMs,
      onRefreshed: (freshToken) => setToken(freshToken),
      onInvalidGrant: handleUnauthorized,
    });
  }, [store, doFetch, handleUnauthorized, nowMs, session]);

  // A tab reopened onto an expired-but-refreshable session: one silent renewal on mount.
  const mountRenewalTried = useRef(false);
  useEffect(() => {
    if (session !== 'guest' || store === null || mountRenewalTried.current) {
      return;
    }
    const stored = loadSsoSession(store);
    if (stored === null || stored.refreshToken === undefined) {
      return;
    }
    mountRenewalTried.current = true;
    void refreshSsoSession(calendarSsoConfig(), store, doFetch, nowMs())
      .then((outcome) => {
        if (outcome.kind === 'refreshed') {
          setToken(outcome.accessToken);
          setUsername(sessionUsername(outcome.accessToken));
          setSession('signed_in');
        }
      })
      .catch(() => {
        // Unreachable provider: stay on the guest prompt; the button retries.
      });
  }, [session, store, doFetch, nowMs]);

  // Complete an ?sso=callback landing on /dashboard, then strip the query and the path state.
  useEffect(() => {
    if (callbackConsumed.current || store === null) {
      return;
    }
    const callback = parseCalendarCallback(window.location.search);
    if (callback.kind === 'none') {
      return;
    }
    callbackConsumed.current = true;
    if (callback.kind === 'provider_error') {
      window.history.replaceState({}, '', HOME_PATH);
      window.dispatchEvent(new PopStateEvent('popstate'));
      setSession('guest');
      showToast('error', t('calendar.gate.failed', { detail: callback.error }));
      return;
    }
    void completeCalendarSignIn(
      calendarSsoConfig(),
      { code: callback.code, state: callback.state },
      window.location.origin,
      store,
      doFetch,
      HOME_PATH,
    ).then((outcome) => {
      window.history.replaceState({}, '', HOME_PATH);
      window.dispatchEvent(new PopStateEvent('popstate'));
      if (outcome.kind === 'signed_in') {
        setToken(outcome.accessToken);
        setUsername(outcome.username);
        setSession('signed_in');
      } else {
        setSession('guest');
        showToast(
          'error',
          outcome.kind === 'stale_state'
            ? t('calendar.gate.stale')
            : t('calendar.gate.failed', { detail: outcome.message }),
        );
      }
    });
  }, [store, doFetch, t]);

  const loadEvents = useCallback(
    (accessToken: string) => {
      setLoad({ kind: 'loading' });
      // 401 recovery: one refresh + one retry before the session counts as unrenewable.
      callWithSsoRecovery(
        accessToken,
        (fresh) =>
          listEvents(
            fresh,
            { from: new Date(rangeFrom).toISOString(), to: new Date(rangeTo).toISOString() },
            doFetch,
          ),
        {
          config: calendarSsoConfig(),
          storage: store,
          fetchImpl: doFetch,
          rejected: (err: unknown): boolean => err instanceof CalendarApiError && err.unauthorized,
          onRefreshed: (freshToken) => setToken(freshToken),
          nowMs,
        },
      )
        .then((events) => setLoad({ kind: 'ready', events }))
        .catch((err: unknown) => {
          if (err instanceof CalendarApiError && err.unauthorized) {
            handleUnauthorized();
            return;
          }
          showToast('error', t('calendar.toasts.api_failed'));
          setLoad({ kind: 'error' });
        });
    },
    [rangeFrom, rangeTo, doFetch, handleUnauthorized, t, store, nowMs],
  );

  useEffect(() => {
    if (token !== null) {
      loadEvents(token);
    }
  }, [token, loadEvents]);

  const signedIn = session === 'signed_in' && token !== null;
  const events = load.kind === 'ready' ? load.events : [];
  const today = todaysEvents(events, at);
  const nearest = nearestEvent(events, at);
  const eventDays = useMemo(
    () => monthEventDays(events, month.getFullYear(), month.getMonth()),
    [events, month],
  );
  const notifications = useMemo(() => deriveNotifications(events, at), [events, at]);

  // Start the PKCE sign-in redirected back to /?sso=callback.
  const handleSignIn = useCallback(async (): Promise<void> => {
    if (store === null) {
      showToast('error', t('sso.notice_storage_blocked'));
      return;
    }
    setSession('exchanging');
    try {
      await startCalendarSignIn(
        calendarSsoConfig(),
        window.location.origin,
        store,
        window.location,
        HOME_PATH,
      );
    } catch {
      setSession('guest');
    }
  }, [store, t]);

  function handleSignOut(): void {
    if (store !== null) {
      forgetSession(store);
    }
    setToken(null);
    setUsername(null);
    setSession('guest');
  }

  function openSchedule(): void {
    if (!signedIn) {
      void handleSignIn();
      return;
    }
    setScheduleOpen(true);
  }

  /**
   * The «Быстрая сессия» quick action (CR-14): one click creates a meeting room through the
   * authenticated create endpoint (a generated slug) and navigates straight into it - the
   * creator becomes the room's owner and bypasses its lobby. Guests are taken into the
   * sign-in flow instead of drawing a 401. A failure keeps the dashboard usable with a toast.
   */
  const [quickBusy, setQuickBusy] = useState(false);
  const handleQuickSession = useCallback((): void => {
    if (!signedIn || token === null) {
      void handleSignIn();
      return;
    }
    if (quickBusy) {
      return;
    }
    setQuickBusy(true);
    createRoom(token, { kind: 'meeting' }, doFetch)
      .then((created): void => {
        navigateTo(`/r/${created.name}`);
      })
      .catch((): void => {
        setQuickBusy(false);
        showToast('error', t('home.create_quick_failed'));
      });
  }, [signedIn, token, quickBusy, handleSignIn, doFetch, t]);

  // The folded landing join form: quick action and nav entries focus its slug input.
  const joinInputRef = useRef<HTMLInputElement | null>(null);
  const focusJoin = useCallback((): void => {
    const input = joinInputRef.current;
    if (input === null) {
      return;
    }
    input.scrollIntoView({ block: 'center' });
    input.focus();
  }, []);

  // The landing's CTA deep links (the /welcome page): ?focus=join focuses the join card's
  // input, ?action=create runs the exact sidebar quick-session flow (its own sign-in gate
  // answers a guest). Consumed once per load - the params are stripped after reading, and
  // the SSO-callback load (exchanging) leaves the decision to the callback flow above.
  const landingActionConsumed = useRef(false);
  useEffect(() => {
    if (landingActionConsumed.current || session === 'exchanging') {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const focus = params.get('focus');
    const action = params.get('action');
    if (focus !== 'join' && action !== 'create') {
      return;
    }
    landingActionConsumed.current = true;
    params.delete('focus');
    params.delete('action');
    const rest = params.toString();
    window.history.replaceState({}, '', HOME_PATH + (rest !== '' ? `?${rest}` : ''));
    if (focus === 'join') {
      focusJoin();
      return;
    }
    handleQuickSession();
  }, [session, focusJoin, handleQuickSession]);
  // The calendar feed lives on this page now: scroll to the «Today» section, and guests are
  // taken into the sign-in flow instead (the calendar sections need the SSO session).
  const openCalendarSection = useCallback((): void => {
    if (!signedIn) {
      void handleSignIn();
      return;
    }
    navigateTo(`/calendar?date=${dayKey(selectedDay ?? at)}`);
  }, [signedIn, handleSignIn, selectedDay, at]);

  // A widget day-click opens the full calendar page on that day (the real grid lives there).
  const openCalendarOnDay = useCallback(
    (day: Date | undefined): void => {
      setSelectedDay(day);
      if (signedIn && day !== undefined) {
        navigateTo(`/calendar?date=${dayKey(day)}`);
      }
    },
    [signedIn],
  );

  const displayName = username ?? (token !== null ? sessionUsername(token) : null);

  return (
    <div
      data-testid="layout-root"
      className="flex h-screen overflow-hidden bg-background text-foreground"
    >
      {sidebarMode !== 'drawer' ? (
        <DashboardSidebar
          mode={sidebarMode}
          todayCount={today.length}
          signedIn={signedIn}
          displayName={displayName}
          onNewEvent={openSchedule}
          onJoin={focusJoin}
          onCalendar={openCalendarSection}
          onRecordings={() => navigateTo('/recordings')}
          onWebinars={() => navigateTo('/webinars')}
          onAnalytics={() => navigateTo('/analytics')}
          onSettings={() => navigateTo('/settings')}
          onSignIn={() => void handleSignIn()}
          onSignOut={handleSignOut}
        />
      ) : null}

      <main className="flex min-w-0 flex-1 flex-col">
        {/* top bar */}
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-card px-4 sm:px-6">
          {sidebarMode === 'drawer' ? (
            <Button
              variant="ghost"
              size="icon"
              className="size-9 text-muted-foreground hover:text-foreground"
              aria-label={t('dashboard.nav.open_menu')}
              data-testid="nav-burger"
              onClick={() => setDrawerOpen(true)}
            >
              <Menu aria-hidden className="size-4" />
            </Button>
          ) : null}
          {sidebarMode === 'drawer' ? (
            /* Small-screen top bar: the mockup renders the compact mark-only brand there
               (SyncraLogo compact), so no wordmark at this size. */
            <BrandMark size="sm" dark={false} />
          ) : null}
          <div className="ml-auto flex items-center gap-2">
            <LangSwitch className="relative" />
            {signedIn ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-[12px] text-muted-foreground hover:text-foreground"
                data-testid="dashboard-signout"
                onClick={handleSignOut}
              >
                <LogOut aria-hidden className="size-3.5" />
                <span className="hidden sm:inline">{t('dashboard.user.sign_out')}</span>
              </Button>
            ) : (
              <Button
                size="sm"
                className="h-8 gap-1.5 text-[12px] font-semibold"
                data-testid="dashboard-signin-top"
                disabled={session === 'exchanging'}
                onClick={() => void handleSignIn()}
              >
                <LogIn aria-hidden className="size-3.5" />
                {t('calendar.gate.sign_in')}
              </Button>
            )}
          </div>
        </div>

        {/* scroll area */}
        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[920px] px-4 py-6 sm:px-6 lg:px-8">
            {/* greeting */}
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[12px] font-medium capitalize text-muted-foreground">
                  {new Intl.DateTimeFormat(locale, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  }).format(at)}
                </p>
                <h1 className="mt-1 truncate text-[26px] font-semibold tracking-tight text-foreground">
                  {signedIn
                    ? t(greetingKey(at), { name: displayName ?? 'SSO' })
                    : t('dashboard.greeting.guest')}
                </h1>
                <p
                  className="mt-1 text-[13.5px] text-muted-foreground"
                  data-testid="dashboard-summary"
                >
                  {signedIn
                    ? today.length === 0
                      ? t('dashboard.greeting.today_zero')
                      : t('dashboard.greeting.today', { count: today.length })
                    : t('dashboard.greeting.guest_hint')}
                </p>
              </div>
            </div>

            {/* quick actions */}
            <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
              <QuickAction
                primary
                icon={Video}
                label={t('dashboard.quick.new_meeting')}
                hint={t('dashboard.quick.new_meeting_hint')}
                testId="quick-new-meeting"
                onClick={openSchedule}
              />
              <QuickAction
                icon={Zap}
                label={t('home.create_quick')}
                hint={t('home.create_quick_hint')}
                testId="quick-session"
                onClick={handleQuickSession}
              />
              <QuickAction
                icon={Link2}
                label={t('dashboard.quick.join')}
                hint={t('dashboard.quick.join_hint')}
                testId="quick-join"
                onClick={focusJoin}
              />
              <QuickAction
                icon={FileVideo2}
                label={t('dashboard.quick.recordings')}
                hint={t('dashboard.quick.recordings_hint')}
                testId="quick-recordings"
                onClick={() => navigateTo('/recordings')}
              />
              <QuickAction
                icon={CalendarDays}
                label={t('dashboard.quick.calendar')}
                hint={t('dashboard.quick.calendar_hint')}
                testId="quick-calendar"
                onClick={openCalendarSection}
              />
            </div>

            {/* the former landing join form, folded into the home dashboard */}
            <JoinRoomCard
              inputRef={joinInputRef}
              token={signedIn ? token : null}
              onRequireSignIn={() => {
                void handleSignIn();
              }}
              fetchImpl={doFetch}
            />

            {/* sign-in prompt replaces the calendar-backed sections for guests */}
            {!signedIn ? (
              <div
                className="mt-8 flex flex-col items-start gap-3 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between"
                data-testid="dashboard-gate"
              >
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-foreground">
                    {t('dashboard.gate.title')}
                  </p>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
                    {t('dashboard.gate.description')}
                  </p>
                </div>
                <Button
                  className="shrink-0 gap-2 font-semibold"
                  data-testid="dashboard-gate-signin"
                  disabled={session === 'exchanging'}
                  onClick={() => void handleSignIn()}
                >
                  {session === 'exchanging' ? (
                    <Loader2 aria-hidden className="size-4 animate-spin" />
                  ) : (
                    <LogIn aria-hidden className="size-4" />
                  )}
                  {t('dashboard.gate.sign_in')}
                </Button>
              </div>
            ) : (
              <>
                {/* today */}
                <section
                  id="dashboard-today"
                  aria-label={t('dashboard.list.today')}
                  className="mt-8 scroll-mt-20"
                >
                  <SectionLabel>{t('dashboard.list.today')}</SectionLabel>
                  {load.kind === 'loading' ? (
                    <div
                      className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-[13px] text-muted-foreground"
                      data-testid="dashboard-events-loading"
                    >
                      <Loader2 aria-hidden className="size-4 animate-spin" />
                      {t('common.loading')}
                    </div>
                  ) : load.kind === 'error' ? (
                    <div
                      className="rounded-xl border border-border bg-card p-4 text-[13px] text-muted-foreground"
                      data-testid="dashboard-events-error"
                    >
                      {t('calendar.toasts.api_failed')}
                    </div>
                  ) : today.length === 0 ? (
                    <div
                      className="rounded-xl border border-border bg-card p-4"
                      data-testid="dashboard-today-empty"
                    >
                      <p className="text-[13px] font-medium text-foreground">
                        {t('dashboard.list.empty')}
                      </p>
                      <p className="mt-0.5 text-[12px] text-muted-foreground">
                        {t('dashboard.list.empty_hint')}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-xl border border-border bg-card">
                      {today.map((event) => (
                        <MeetingRow
                          key={eventKey(event)}
                          event={event}
                          now={at}
                          locale={locale}
                          onDetails={() => setDetailsEvent(event)}
                          onOpenRoom={(slug) => navigateTo(`/r/${slug}`)}
                        />
                      ))}
                    </div>
                  )}
                </section>

                {/* promo card: the webinar surface (the workshop kind merged into it, 0015) */}
                <section aria-label={t('dashboard.promo.aria')} className="mt-8">
                  <SectionLabel>{t('dashboard.promo.title')}</SectionLabel>
                  <div className="grid gap-3">
                    <div className="relative overflow-hidden rounded-xl border border-border bg-card p-5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-emerald-400">
                          <Radio aria-hidden className="size-3" />
                          {t('dashboard.promo.webinars')}
                        </span>
                        <span
                          className="rounded border border-border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                          data-testid="promo-soon-badge"
                        >
                          {t('dashboard.promo.soon')}
                        </span>
                      </div>
                      <p className="mt-3 text-[14px] font-semibold text-foreground">
                        {t('dashboard.promo.webinars_title')}
                      </p>
                      <p className="mt-1 text-[12px] text-muted-foreground">
                        {t('dashboard.promo.webinars_hint')}
                      </p>
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>
        </div>
      </main>

      {/* right rail: calendar-backed, so it renders for signed-in sessions from xl up */}
      {signedIn ? (
        <aside className="scroll-thin hidden w-[300px] shrink-0 flex-col gap-4 overflow-y-auto border-l border-border bg-card px-4 py-6 xl:flex">
          <div className="rounded-xl border border-border p-4" data-testid="dashboard-nearest">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {t('dashboard.nearest.label')}
            </p>
            {nearest === null ? (
              <p className="mt-3 text-[13px] text-muted-foreground">
                {t('dashboard.nearest.none')}
              </p>
            ) : (
              <>
                <div className="mt-3 text-[13.5px] font-semibold leading-snug text-foreground">
                  {nearest.title}
                </div>
                <div className="mt-1 text-[11.5px] tabular-nums text-muted-foreground">
                  {new Intl.DateTimeFormat(locale, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  }).format(eventStart(nearest))}
                  {' · '}
                  {nearest.allDay
                    ? t('calendar.list.all_day')
                    : new Intl.DateTimeFormat(locale, {
                        hour: '2-digit',
                        minute: '2-digit',
                      }).format(eventStart(nearest))}
                </div>
                {nearest.roomSlug !== null ? (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Link2 aria-hidden className="size-3 shrink-0" />
                    <span className="truncate font-mono">/r/{nearest.roomSlug}</span>
                  </div>
                ) : null}
                <div className="mt-3.5 flex gap-2">
                  {nearest.roomSlug !== null ? (
                    <Button
                      size="sm"
                      className="h-8 flex-1 gap-1.5 text-[12.5px] font-semibold"
                      data-testid="nearest-join"
                      onClick={() => navigateTo(`/r/${nearest.roomSlug}`)}
                    >
                      <Video aria-hidden className="size-3.5" />
                      {t('dashboard.nearest.join')}
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 flex-1 text-[12.5px] font-semibold"
                    data-testid="nearest-details"
                    onClick={() => setDetailsEvent(nearest)}
                  >
                    {t('dashboard.nearest.details')}
                  </Button>
                </div>
              </>
            )}
          </div>

          <CalendarWidget
            month={month}
            onMonthChange={setMonth}
            selected={selectedDay}
            onSelect={openCalendarOnDay}
            eventDays={eventDays}
            today={new Date(at.getFullYear(), at.getMonth(), at.getDate())}
          />

          <div data-testid="dashboard-notifications">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {t('dashboard.notifications.title')}
            </p>
            {notifications.length === 0 ? (
              <div className="rounded-lg border border-border/70 bg-muted/40 p-3 text-[12px] text-muted-foreground">
                {t('dashboard.notifications.empty')}
              </div>
            ) : (
              <div className="space-y-2.5">
                {notifications.map((item) => (
                  <div
                    key={`${item.kind}:${eventKey(item.event)}`}
                    className="flex gap-2.5 rounded-lg border border-border/70 bg-muted/40 p-3"
                  >
                    <span
                      aria-hidden
                      className={
                        item.kind === 'live'
                          ? 'mt-1.5 size-1.5 shrink-0 animate-pulse rounded-full bg-emerald-500'
                          : 'mt-1.5 size-1.5 shrink-0 rounded-full bg-amber-500'
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12.5px] font-medium leading-snug text-foreground">
                        {item.kind === 'live'
                          ? t('dashboard.notifications.live', { title: item.event.title })
                          : t('dashboard.notifications.soon', {
                              time: new Intl.DateTimeFormat(locale, {
                                hour: '2-digit',
                                minute: '2-digit',
                              }).format(eventStart(item.event)),
                              title: item.event.title,
                            })}
                      </div>
                      {item.event.roomSlug !== null ? (
                        <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
                          /r/{item.event.roomSlug}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      ) : null}

      {/* off-canvas drawer navigation below md */}
      {drawerOpen && sidebarMode === 'drawer' ? (
        <div className="fixed inset-0 z-50 xl:hidden" data-testid="nav-drawer">
          <button
            type="button"
            aria-label={t('dashboard.nav.close_menu')}
            data-testid="nav-drawer-backdrop"
            className="absolute inset-0 h-full w-full bg-black/60"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 flex w-64 max-w-[85vw] flex-col border-r border-border bg-card">
            <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
              {/* Drawer header carries the same compact mark-only brand as the top bar. */}
              <BrandMark size="sm" dark={false} />
              <button
                type="button"
                aria-label={t('dashboard.nav.close_menu')}
                data-testid="nav-drawer-close"
                className="ml-auto rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                onClick={() => setDrawerOpen(false)}
              >
                <X aria-hidden className="size-4" />
              </button>
            </div>
            <nav
              aria-label={t('dashboard.nav.menu')}
              className="scroll-thin flex-1 space-y-0.5 overflow-y-auto p-3"
            >
              <NavItem
                icon={Video}
                label={t('dashboard.nav.meetings')}
                active
                onClick={() => setDrawerOpen(false)}
              />
              <NavItem
                icon={CalendarDays}
                label={t('dashboard.nav.calendar')}
                onClick={() => {
                  setDrawerOpen(false);
                  openCalendarSection();
                }}
              />
              <NavItem
                icon={FileVideo2}
                label={t('dashboard.nav.recordings')}
                onClick={() => {
                  setDrawerOpen(false);
                  navigateTo('/recordings');
                }}
              />
              <NavItem
                icon={Link2}
                label={t('dashboard.nav.join')}
                onClick={() => {
                  setDrawerOpen(false);
                  focusJoin();
                }}
              />
              <NavItem
                icon={Radio}
                label={t('dashboard.nav.webinars')}
                onClick={() => {
                  setDrawerOpen(false);
                  navigateTo('/webinars');
                }}
              />
              <NavItem
                icon={BarChart3}
                label={t('dashboard.nav.analytics')}
                onClick={() => {
                  setDrawerOpen(false);
                  navigateTo('/analytics');
                }}
              />
              <NavItem
                icon={SettingsIcon}
                label={t('dashboard.nav.settings')}
                onClick={() => {
                  setDrawerOpen(false);
                  navigateTo('/settings');
                }}
              />
            </nav>
            <div className="border-t border-border/70 p-3">
              <Button className="w-full justify-start gap-2" onClick={openSchedule}>
                <Plus aria-hidden className="size-4" />
                {t('dashboard.nav.new_event')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {token !== null ? (
        <ScheduleDialog
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          token={token}
          initialType="meeting"
          onCreated={() => loadEvents(token)}
        />
      ) : null}
      <EventSidebar
        open={detailsEvent !== null}
        onOpenChange={(next) => {
          if (!next) {
            setDetailsEvent(null);
          }
        }}
        token={token ?? ''}
        target={detailsEvent !== null ? { kind: 'details', event: detailsEvent } : null}
        onSaved={() => {
          if (token !== null) {
            loadEvents(token);
          }
        }}
        onDeleted={() => {
          setDetailsEvent(null);
          if (token !== null) {
            loadEvents(token);
          }
        }}
        onOpenRoom={(slug) => navigateTo(`/r/${slug}`)}
      />
      <ToastStack />
    </div>
  );
}

function greetingKey(now: Date): string {
  const hour = now.getHours();
  if (hour < 12) {
    return 'calendar.topbar.greeting_morning';
  }
  if (hour < 18) {
    return 'calendar.topbar.greeting_afternoon';
  }
  return 'calendar.topbar.greeting_evening';
}

// ---------- Sidebar ---------------------------------------------------------------------------

interface NavItemProps {
  readonly icon: ComponentType<{ className?: string }>;
  readonly label: string;
  readonly active?: boolean | undefined;
  readonly badge?: string | undefined;
  readonly count?: number | undefined;
  readonly disabled?: boolean | undefined;
  readonly onClick?: (() => void) | undefined;
}

/** One navigation entry; the rail variant hides the label (title + sr-only keep it accessible). */
function NavItem({
  icon: Icon,
  label,
  active = false,
  badge,
  count,
  disabled = false,
  onClick,
}: NavItemProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-current={active ? 'page' : undefined}
      title={label}
      className={
        disabled
          ? 'flex w-full cursor-not-allowed items-center gap-2.5 rounded-lg px-3 py-2 text-[13.5px] font-medium text-muted-foreground/50'
          : active
            ? 'flex w-full items-center gap-2.5 rounded-lg bg-primary px-3 py-2 text-[13.5px] font-medium text-primary-foreground shadow-sm'
            : 'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13.5px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
      }
    >
      <Icon className="size-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      {typeof count === 'number' && count > 0 ? (
        <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10.5px] font-semibold tabular-nums text-muted-foreground">
          {count}
        </span>
      ) : null}
      {badge !== undefined ? (
        <span className="shrink-0 rounded border border-border px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

interface DashboardSidebarProps {
  readonly mode: 'full' | 'icons';
  readonly todayCount: number;
  readonly signedIn: boolean;
  readonly displayName: string | null;
  readonly onNewEvent: () => void;
  readonly onJoin: () => void;
  readonly onCalendar: () => void;
  readonly onRecordings: () => void;
  readonly onWebinars: () => void;
  readonly onAnalytics: () => void;
  readonly onSettings: () => void;
  readonly onSignIn: () => void;
  readonly onSignOut: () => void;
}

/**
 * The persistent sidebar: full (labels, new-event CTA, account footer) from lg, icon-only rail
 * on md..lg. The drawer variant below md is rendered by the page itself as an overlay.
 */
function DashboardSidebar({
  mode,
  todayCount,
  signedIn,
  displayName,
  onNewEvent,
  onJoin,
  onCalendar,
  onRecordings,
  onWebinars,
  onAnalytics,
  onSettings,
  onSignIn,
  onSignOut,
}: DashboardSidebarProps): JSX.Element {
  const { t } = useTranslation();
  const icons = mode === 'icons';
  return (
    <aside
      data-testid="dashboard-sidebar"
      className={
        icons
          ? 'hidden w-14 shrink-0 flex-col border-r border-border bg-card md:flex'
          : 'hidden w-60 shrink-0 flex-col border-r border-border bg-card lg:flex'
      }
    >
      <div className={icons ? 'px-3 pb-4 pt-5' : 'px-5 pb-4 pt-5'}>
        {/* Sidebar brand: the full Syncra Workspace lockup at rest, the compact mark in the
            icons rail - the mockup's SyncraLogo / SyncraLogo compact pair. Clicking it opens
            the Syncra landing (/welcome), the workspace's front door. */}
        <button
          type="button"
          data-testid="sidebar-brand"
          aria-label={t('landing.open_landing_aria')}
          onClick={() => {
            navigateTo('/welcome');
          }}
          className="rounded-md text-left transition-opacity hover:opacity-80"
        >
          {icons ? <BrandMark size="sm" dark={false} /> : <BrandLockup size="md" dark={false} />}
        </button>
      </div>
      <div className={icons ? 'px-2' : 'px-3'}>
        <Button
          className={icons ? 'w-full justify-center px-0' : 'w-full justify-start gap-2'}
          data-testid="sidebar-new-event"
          title={t('dashboard.nav.new_event')}
          onClick={onNewEvent}
        >
          <Plus aria-hidden className="size-4 shrink-0" />
          {icons ? (
            <span className="sr-only">{t('dashboard.nav.new_event')}</span>
          ) : (
            t('dashboard.nav.new_event')
          )}
        </Button>
      </div>
      <nav
        aria-label={t('dashboard.nav.menu')}
        className="scroll-thin mt-6 flex-1 space-y-0.5 overflow-y-auto px-2 sm:px-3"
      >
        <NavItem
          icon={Video}
          label={t('dashboard.nav.meetings')}
          active
          count={icons ? undefined : todayCount}
        />
        <NavItem icon={CalendarDays} label={t('dashboard.nav.calendar')} onClick={onCalendar} />
        <NavItem icon={FileVideo2} label={t('dashboard.nav.recordings')} onClick={onRecordings} />
        <NavItem icon={Link2} label={t('dashboard.nav.join')} onClick={onJoin} />
        <NavItem icon={Radio} label={t('dashboard.nav.webinars')} onClick={onWebinars} />
        <NavItem icon={BarChart3} label={t('dashboard.nav.analytics')} onClick={onAnalytics} />
        <NavItem icon={SettingsIcon} label={t('dashboard.nav.settings')} onClick={onSettings} />
      </nav>
      <div className="border-t border-border/70 p-3">
        {signedIn ? (
          <div className="flex items-center gap-2.5 rounded-lg px-1 py-1">
            <UserAvatar name={displayName ?? 'SSO'} size="sm" />
            <div className="min-w-0 flex-1 leading-tight">
              <div
                className="truncate text-[13px] font-semibold text-foreground"
                data-testid="sidebar-username"
              >
                {displayName ?? 'SSO'}
              </div>
              <div className="truncate text-[11px] text-muted-foreground">SSO</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
              aria-label={t('dashboard.user.sign_out')}
              data-testid="sidebar-signout"
              onClick={onSignOut}
            >
              <LogOut aria-hidden className="size-3.5" />
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            className="w-full gap-2"
            data-testid="sidebar-signin"
            onClick={onSignIn}
          >
            <LogIn aria-hidden className="size-4 shrink-0" />
            {icons ? (
              <span className="sr-only">{t('calendar.gate.sign_in')}</span>
            ) : (
              t('calendar.gate.sign_in')
            )}
          </Button>
        )}
      </div>
    </aside>
  );
}

// ---------- Main-area atoms -------------------------------------------------------------------

function SectionLabel({ children }: { children: JSX.Element | string }): JSX.Element {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {children}
      </h2>
    </div>
  );
}

interface QuickActionProps {
  readonly icon: ComponentType<{ className?: string }>;
  readonly label: string;
  readonly hint: string;
  readonly testId: string;
  readonly onClick: () => void;
  readonly primary?: boolean;
}

function QuickAction({
  icon: Icon,
  label,
  hint,
  testId,
  onClick,
  primary = false,
}: QuickActionProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        primary
          ? 'group min-w-0 rounded-xl bg-primary p-4 text-left text-primary-foreground transition-colors hover:bg-primary/90'
          : 'group min-w-0 overflow-hidden rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-ring/50'
      }
    >
      <span
        className={
          primary
            ? 'flex size-9 items-center justify-center rounded-lg bg-primary-foreground/10'
            : 'flex size-9 items-center justify-center rounded-lg bg-muted text-muted-foreground'
        }
      >
        <Icon className="size-4" />
      </span>
      <div
        className={
          primary
            ? 'mt-6 truncate text-[13.5px] font-semibold'
            : 'mt-6 truncate text-[13.5px] font-semibold text-foreground'
        }
      >
        {label}
      </div>
      <div className="mt-0.5 flex items-center gap-1 text-[11.5px] text-muted-foreground">
        <span className="truncate">{hint}</span>
        <ChevronRight
          aria-hidden
          className="size-3 shrink-0 transition-transform group-hover:translate-x-0.5"
        />
      </div>
    </button>
  );
}

interface MeetingRowProps {
  readonly event: CalendarEvent;
  readonly now: Date;
  readonly locale: string;
  readonly onDetails: () => void;
  readonly onOpenRoom: (slug: string) => void;
}

/** One «Сегодня» row: time column, title + status badges, people, room entry. */
function MeetingRow({ event, now, locale, onDetails, onOpenRoom }: MeetingRowProps): JSX.Element {
  const { t } = useTranslation();
  const live = isEventLive(event, now);
  const start = eventStart(event);
  const end = eventEnd(event);
  const sourceLabel =
    event.source === 'local'
      ? t('calendar.list.local_badge')
      : event.source === 'caldav'
        ? t('calendar.list.source_caldav')
        : event.source === 'ews'
          ? t('calendar.list.source_ews')
          : t('calendar.list.source_ad');
  const shownParticipants = event.participants.slice(0, 3);
  const restParticipants = event.participants.length - shownParticipants.length;
  return (
    <div
      data-testid={`dashboard-event-${eventKey(event)}`}
      className={
        live
          ? 'group flex min-w-0 items-center gap-3 bg-accent/60 px-4 py-3.5 sm:gap-4'
          : 'group flex min-w-0 items-center gap-3 px-4 py-3.5 transition-colors hover:bg-accent/50 sm:gap-4'
      }
    >
      <div className="w-[52px] shrink-0 text-center">
        <div
          className={
            live
              ? 'text-[13.5px] font-semibold tabular-nums text-emerald-400'
              : 'text-[13.5px] font-semibold tabular-nums text-foreground'
          }
        >
          {event.allDay
            ? t('calendar.list.all_day')
            : new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(start)}
        </div>
        {event.allDay ? null : (
          <div className="text-[11px] tabular-nums text-muted-foreground">
            {new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(end)}
          </div>
        )}
      </div>
      <div
        aria-hidden
        className={live ? 'h-9 w-px shrink-0 bg-emerald-400' : 'h-9 w-px shrink-0 bg-border'}
      />
      <button
        type="button"
        onClick={onDetails}
        className="min-w-0 flex-1 text-left"
        data-testid={`dashboard-event-details-${eventKey(event)}`}
      >
        <span className="flex flex-wrap items-center gap-2">
          <span className="max-w-full truncate text-[14px] font-medium text-foreground">
            {event.title}
          </span>
          {live ? (
            <span
              className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-[10.5px] font-semibold text-emerald-400"
              data-testid="dashboard-live-badge"
            >
              <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-emerald-500" />
              {t('dashboard.list.live_badge')}
            </span>
          ) : null}
          <span className="rounded-md border border-border px-1.5 py-0.5 text-[10.5px] text-muted-foreground">
            {sourceLabel}
          </span>
        </span>
        <span className="mt-1.5 flex min-w-0 items-center gap-2.5">
          {event.organizer !== null ? (
            <>
              <UserAvatar name={event.organizer} size="sm" ring />
              <span className="hidden truncate text-[11.5px] text-muted-foreground sm:block">
                {t('dashboard.list.organizer', { name: event.organizer })}
              </span>
            </>
          ) : null}
          {shownParticipants.length > 0 ? (
            <span className="flex shrink-0 -space-x-1.5">
              {shownParticipants.map((participant) => (
                <UserAvatar
                  key={participant}
                  name={participant}
                  size="sm"
                  className="ring-2 ring-card"
                />
              ))}
            </span>
          ) : null}
          {restParticipants > 0 ? (
            <span className="shrink-0 text-[11px] font-medium tabular-nums text-muted-foreground">
              +{restParticipants}
            </span>
          ) : null}
        </span>
      </button>
      {event.roomSlug !== null ? (
        <Button
          size="sm"
          variant={live ? 'default' : 'outline'}
          className="h-8 shrink-0 gap-1.5 px-3 text-[12.5px] font-semibold"
          data-testid={`dashboard-event-join-${eventKey(event)}`}
          onClick={() => onOpenRoom(event.roomSlug ?? '')}
        >
          <Video aria-hidden className="size-3.5" />
          {t('dashboard.list.enter')}
        </Button>
      ) : (
        <span
          aria-hidden
          className="hidden shrink-0 text-[12px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 sm:block"
        >
          {t('dashboard.list.details')}
        </span>
      )}
    </div>
  );
}
